import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Flex, VStack } from '@chakra-ui/react';
import { MdPlayCircle } from 'react-icons/md';

import Image from '@rsc/ui-components/tailwind/Image';
import BlogExpertBadge from '@rsc/ui-components/tailwind/BlogExpertBadge';
import Link from '@rsc/ui-components/tailwind/Link';

export default function CardRowThin({ post, badge, onClick }) {
  if (!post) return null;

  const isVideo = /video/i.test(post?.articleType);

  return (
    <VStack
      borderRadius="24px"
      boxShadow={['none', null, '0px 4px 56px 0px rgba(5, 37, 77, 0.15)']}
      _hover={{
        border: '2px solid',
        borderColor: 'brand.sunny',
        textDecor: 'none',
        margin: '-2px',
      }}
      pos="relative"
      background="white"
      gap={0}
      onClick={onClick}
    >
      {post.author?.title && post.author?.slug?.current && badge?.headline && (
        <BlogExpertBadge
          className="z-50 absolute top-2 left-2"
          author={post.author?.title}
          slug={post.author?.slug?.current}
          badge={badge}
        />
      )}
      <Box
        as={Link}
        className="rounded-t-3xl overflow-hidden"
        link={`/blog/${post.slug?.current}`}
        width="100%"
        h="476px"
      >
        <Box pos="relative">
          <Image className="min-h-[302px]" sanityImage={post.image} />
        </Box>
        <Box
          borderRadius="24px"
          background="white"
          p={6}
          height={['167px', null, 'auto']}
        >
          <Flex mb={3} flexDirection="row" flexWrap="wrap">
            {post.categories?.length &&
              post.categories?.map((category, index) => (
                <Text
                  key={category._id}
                  fontWeight="semibold"
                  color="typography.light"
                  mr={1}
                >
                  {category.title}
                  {index !== post.categories.length - 1 && ' • '}
                </Text>
              ))}

            {isVideo && (
              <Flex alignItems="center" gap={1}>
                <Text fontWeight="semibold" color="typography.light">
                  {post.categories?.length && ' • '}
                  {post.articleType}
                </Text>
                <Text color="typography.light">
                  <MdPlayCircle size={20} />
                </Text>
              </Flex>
            )}
          </Flex>
          <Text fontSize="bodyLarge" fontWeight="semibold" noOfLines={3}>
            {post.title}
          </Text>
        </Box>
      </Box>
    </VStack>
  );
}

CardRowThin.propTypes = {
  post: PropTypes.shape({
    title: PropTypes.string.isRequired,
    articleType: PropTypes.string,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        title: PropTypes.string,
      })
    ),
    image: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      alt: PropTypes.string,
    }).isRequired,
    author: PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.shape({
        current: PropTypes.string.isRequired,
      }).isRequired,
    }),
    slug: PropTypes.shape({
      current: PropTypes.string.isRequired,
    }).isRequired,
  }),
  badge: PropTypes.shape({
    headline: PropTypes.string,
    copy: PropTypes.string,
    link: PropTypes.shape({
      linkType: PropTypes.string,
      href: PropTypes.string,
      internalLink: PropTypes.shape({
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
      }),
    }),
    icon: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
  onClick: PropTypes.func,
};

CardRowThin.defaultProps = {
  post: {
    articleType: null,
    categories: [],
  },
  badge: null,
  onClick: () => {},
};
